import { ResizeObserver } from '@juggle/resize-observer';
import { PerspectiveCamera } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { ErrorBoundary } from '@sentry/react';
import { ReactElement, FunctionComponent, memo, Suspense, useState, useMemo, useEffect } from 'react';
import { Jute } from '../index';
import { IMode, ISceneActions, ITransCatTypes, useDataStore, useSceneStore } from '../zustand';
import OrbitControlsWrapper from './OrbitControlsWrapper';
import { px, nx, py, ny, pz, nz } from './r3f-components/components/Lighting';
import Lighting from './r3f-components/components/Lighting/Lighting';
import { isSafariWithAntialiasingBug } from './r3f-components/utils/general';
import ArScene from './Scene/ArScene';
import ScreenRelativeScene from './Scene/ScreenRelativeScene';
import { TrackerGroups } from './TrackerGroups/TrackerGroups';
import ZapparCameraWrapper from './ZapparCameraWrapper';
import VisibilityController from './VisibilityController';
import { useGetActiveTrackingImageRadius, useGetActiveTrackingType, useHasOnly360ContentScenes, useIsActiveTrackingImageFlatOrientation } from './hoc/hocCustomHooks';
import { ITrackingTypes } from './r3f-components/component-data-structure';
import { FACE_TRACKING_AR_WEBEMBED_SHADOWPLANE_POSITION, LIGHTING_DIRECTIONAL_POSITION_UPRIGHT_IMAGE_TRACKED } from './r3f-components/utils/constants';
import ShadowPlane from './r3f-components/components/ShadowPlane';
import PresetAnimationControllerComponent from './PresetAnimationController';
import Content360Wrapper from './TrackerGroups/Content360Wrapper/Content360Wrapper';
import BVHGenerationControllerComponent from './BVHGenerationControllerComponent';
import { IImageSceneDict } from './Root';
// import TestModel from './r3f-components/components/TestModel/TestModel';

interface IParentProps {
	children: {
		imgTracked: IImageSceneDict;
		wldTracked: ReactElement[];
		faceTracked: ReactElement[];
		content360: ReactElement[];
	} | null;
}

export const ZapparCanvas: FunctionComponent<IParentProps> = ({ children: scenes }) => {
	const [isLaunched, setIsLaunched] = useState(false);
	// const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
	const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId);
	const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled ?? true)
	const activeSceneTrackingType = useGetActiveTrackingType();
	const webXR = useDataStore(({ webXR }) => webXR);
	const isFlatOrientation = useIsActiveTrackingImageFlatOrientation();
	const trackingImageRadius = useGetActiveTrackingImageRadius();
	const mode = useDataStore(({ mode }) => mode);
	const projectHasOnly360ContentScenes = useHasOnly360ContentScenes();

	const directionalLightPosition = activeSceneTrackingType === ITrackingTypes.image && !isFlatOrientation ? LIGHTING_DIRECTIONAL_POSITION_UPRIGHT_IMAGE_TRACKED : undefined;

	useEffect(() => {
		Jute.onLaunch(() => {
			setIsLaunched(true);
		});
	}, [setIsLaunched]);

	useEffect(() => {
		// If camera never switched on (web embed and test environment), mimic first camera frame call
		if (isLaunched && mode === IMode.nonar) {
			Jute.firstCameraFrame();
		}
		if (isLaunched && Jute.environment === 'test') {
			// usually first scene enter transition triggered in the respective tracking groups via 'place' button
			// click (world tracked scenes) or  via first time target image visibility (image tracked scenes).
			// for testing purposes we need to trigger first the enter of the first scene transition here with a bit of a delay.
			Jute.firstCameraFrame();
			setTimeout(() => {
				const activeSceneId = useSceneStore.getState().activeSceneId!;
				sceneDispatch?.({
					type: ISceneActions.SET_TRANSITION_CATEGORY,
					transCatDict: { [activeSceneId]: ITransCatTypes.enter },
				});
			}, 1000);
		}
	}, [isLaunched, mode, sceneDispatch]);

	useEffect(() => {
		if (!Jute.isIframe || !activeSceneId) return;
		sceneDispatch?.({
			type: ISceneActions.SET_TRANSITION_CATEGORY,
			transCatDict: { [activeSceneId]: ITransCatTypes.enter },
		});
	}, [activeSceneId, sceneDispatch]);

	let shadows: boolean | 'basic' = shadowsEnabled;
	if (shadows && Jute.isZapbox) shadows = 'basic';

	return (
		<ErrorBoundary onError={Jute.error}>
			<Canvas
				id="zapparCanvas"
				resize={useMemo(() => ({ polyfill: ResizeObserver }), [])}
				style={useMemo(() => ({ position: 'absolute', left: '0', top: '0' }), [])}
				dpr={window.devicePixelRatio}
				raycaster={{firstHitOnly: true}}
				flat
				shadows={shadows}
				gl={{ antialias: !isSafariWithAntialiasingBug() && mode === IMode.nonar }}
				onCreated={({ scene, gl }) => {
					if (typeof (window as any).__THREE_DEVTOOLS__ !== 'undefined') {
						(window as any).__THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('observe', { detail: scene }));
						(window as any).__THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('observe', { detail: gl }));
					}
				}}
			>
				<Suspense fallback={null}>
					{mode === IMode.nonar && <VisibilityController />}
					<PresetAnimationControllerComponent />
					<BVHGenerationControllerComponent />
					<ArScene>
						{/* <TestModel /> */}
						<Lighting envMapFiles={!!px && mode === IMode.nonar ? [px, nx, py, ny, pz, nz] : undefined} enableDefaultEnvironment={activeSceneTrackingType !== ITrackingTypes.content360} directionalLightPosition={directionalLightPosition} environmentSceneName={mode === IMode.nonar ? 'ArScene' : undefined} />	
						{/* Mount zappar camera if launch button clicked, its in AR mode (not web embedded) and has a mix of 360 content and other scene types and not in test mode */}
						{(!projectHasOnly360ContentScenes && projectHasOnly360ContentScenes !== null) && isLaunched && mode === IMode.ar && Jute.environment !== 'test' && (webXR === null || !Jute.isHeadset) && <ZapparCameraWrapper />}
						{/* Mount if in non-ar context or the project has only 360 scenes and force a 50 fov camera */}
						{mode === IMode.nonar && <PerspectiveCamera name="non-ar-camera" fov={50} makeDefault />}

						{/* Mount content in tracker groups if in AR mode (not web embedded) and not in test mode */}
						{mode === IMode.ar && Jute.environment !== 'test' && <TrackerGroups>{scenes}</TrackerGroups>}
						{/* Mount content in non-tracked group if web embedded or in test mode */}
						{(mode === IMode.nonar || Jute.environment === 'test') && (
							<group position={[0, 0, 0]} rotation={[0, 0, 0]} scale={[1, 1, 1]}>
								{scenes?.wldTracked}
								{scenes?.imgTracked && Object.values(scenes.imgTracked ?? {}).reduce((acc, curr) => [...acc, ...curr?.map(d => d.content)], [] as ReactElement[])}
								{scenes?.faceTracked}
								<Content360Wrapper enabled={activeSceneTrackingType === ITrackingTypes.content360} >
									{scenes?.content360}
								</Content360Wrapper>
								{!!trackingImageRadius && activeSceneTrackingType === ITrackingTypes.image ? null : <ShadowPlane 
									position={activeSceneTrackingType === ITrackingTypes.face ? FACE_TRACKING_AR_WEBEMBED_SHADOWPLANE_POSITION : [0, 0, 0]} 
									rotation={[activeSceneTrackingType === ITrackingTypes.image && !isFlatOrientation ? 0 : -Math.PI / 2, 0, 0]}
								/>}
							</group>
						)}
						{mode == IMode.nonar && activeSceneTrackingType !== ITrackingTypes.content360 && <OrbitControlsWrapper />}
					</ArScene>
					{(webXR === null || webXR === 'mobile') && <ScreenRelativeScene>
						<Lighting envMapFiles={!!px && mode === IMode.nonar ? [px, nx, py, ny, pz, nz] : undefined} enableDefaultEnvironment={activeSceneTrackingType !== ITrackingTypes.content360} />
					</ScreenRelativeScene>}
				</Suspense>
			</Canvas>
		</ErrorBoundary>
	);
};

ZapparCanvas.displayName = 'ZapparCanvas';
export default memo(ZapparCanvas);
